import React, { useState, useEffect, useRef } from "react";
import { Edit, Save, X, RotateCcw } from "lucide-react";
import {
  saveBulkExpenseDeviation,
  getExpenseActual,
  getExpenseForecast,
  getExpenseDeviation,
} from "../../../utils/ActionCreator";
import moment from "moment";
import "./clickAnimation.css";
import "./responsiveStyles.css";

/* ─── Theme tokens ────────────────────────────────────────────────────────── */
const T = {
  navy:        "#263145",
  teal:        "#025954",
  tealMid:     "#4fd1c5",
  white:       "#ffffff",
  surfaceAlt:  "#f8fafc",
  border:      "#e4eaf2",
  borderDark:  "#d0d9e6",
  text:        "#1a2845",
  textMid:     "#3d526e",
  textMuted:   "#8596ae",
  textLight:   "#b2becc",
  red:         "#e53e3e",
  redBg:       "rgba(229,62,62,0.07)",
  green:       "#16a085",
  greenBg:     "rgba(22,160,133,0.07)",
};

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === 0) return "—";
  const num = typeof amount === "string" ? parseFloat(amount.replace(/[₹,]/g, "")) : amount;
  if (isNaN(num)) return "—";
  if (num < 0) return `-₹${Math.abs(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const m = moment(dateStr, ["YYYY-MM-DD", "DD-MM-YYYY", "MM/DD/YYYY"], true);
  return m.isValid() ? m.format("DD MMM YYYY") : dateStr;
};

const getVarianceColor = (v) => v > 0 ? T.red   : v < 0 ? T.green  : T.textLight;
const getVarianceBg    = (v) => v > 0 ? T.redBg : v < 0 ? T.greenBg : "transparent";
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

/* ─── Flatten API → display rows ─────────────────────────────────────────── */
const flattenApiResponse = (response) => {
  if (!Array.isArray(response) || response.length === 0) return [];
  const rows = [];
  response.forEach((cat) => {
    const categoryName = (cat.categoryName || "Uncategorized").trim();
    const catBudget    = parseFloat(cat.budget) || 0;
    const catActual    = parseFloat(cat.actual) || 0;
    rows.push({
      _key: `${categoryName}__summary`,
      _isSummary: true,
      categoryName,
      subCategoryName: "",
      forecastBudget: catBudget,
      actual: catActual,
      expenseDate: "", paidTo: "", paymentMode: "",
    });
    (cat.subCategories || []).forEach((sub, subIdx) => {
      const subName   = (sub.subCategoryName || "").trim();
      const subBudget = parseFloat(sub.budget) || 0;
      const details =
        Array.isArray(sub.actualDetails) && sub.actualDetails.length > 0
          ? sub.actualDetails
          : [{ expenseDate: "", paidTo: "", paymentMode: "", amount: 0 }];
      details.forEach((detail, dIdx) => {
        rows.push({
          _key: `${categoryName}__${subName || "unnamed"}__${subIdx}__${dIdx}`,
          _isSummary: false,
          categoryName,
          subCategoryName: subName || "—",
          forecastBudget: subBudget,
          actual: parseFloat(detail.amount) || 0,
          expenseDate: detail.expenseDate || "",
          paidTo:      detail.paidTo      || "",
          paymentMode: detail.paymentMode || "",
        });
      });
    });
  });
  return rows;
};

/* ─── Columns ─────────────────────────────────────────────────────────────── */
const COLUMNS = [
  { key: "date",        label: "Date",         flex: 1.0 },
  { key: "category",    label: "Category",     flex: 1.4 },
  { key: "subcategory", label: "Sub-Category", flex: 1.4 },
  { key: "vendor",      label: "Vendor",       flex: 1.2 },
  { key: "forecast",    label: "Forecast",     flex: 1   },
  { key: "actual",      label: "Actual",       flex: 1   },
  { key: "variance",    label: "Variance",     flex: 1   },
  { key: "payment",     label: "Payment Mode", flex: 1   },
];

/* ─── EditableText ────────────────────────────────────────────────────────── */
const EditableText = ({ value, isEditing, isLocked, onCommit, type = "text", placeholder = "—" }) => {
  const [active, setActive] = useState(false);
  const [val,    setVal]    = useState("");
  const ref = useRef(null);

  useEffect(() => { if (!isEditing) setActive(false); }, [isEditing]);
  useEffect(() => { if (active && ref.current) ref.current.focus(); }, [active]);

  const start = () => {
    if (!isEditing || isLocked) return;
    setActive(true);
    setVal(type === "number" ? (value === 0 ? "" : String(value ?? "")) : (value || ""));
  };

  const commit = () => {
    setActive(false);
    if (!onCommit) return;
    if (type === "number") { const p = parseFloat(val); onCommit(isNaN(p) ? 0 : p); }
    else onCommit(val);
  };

  if (active) {
    return (
      <input
        ref={ref}
        type={type === "number" ? "number" : type === "date" ? "date" : "text"}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setActive(false); }}
        style={{
          width: "90%", height: "30px",
          border: `1.5px solid ${T.teal}`,
          borderRadius: "6px",
          padding: "2px 10px",
          fontSize: "12.5px",
          fontWeight: "600",
          textAlign: "center",
          backgroundColor: "rgba(2,89,84,0.04)",
          color: T.text,
          outline: "none",
          boxShadow: "0 0 0 3px rgba(2,89,84,0.10)",
          fontFamily: "'DM Sans', sans-serif",
        }}
      />
    );
  }

  const displayValue =
    type === "number" ? formatCurrency(value) :
    type === "date"   ? formatDate(value) :
    (value || placeholder);

  return (
    <span
      onClick={start}
      style={{
        fontSize: "12.5px",
        fontWeight: "600",
        color: isLocked ? T.textMid : T.textMuted,
        cursor: isEditing && !isLocked ? "pointer" : "default",
        borderBottom: isEditing && !isLocked ? `1.5px dashed ${T.tealMid}` : "none",
        paddingBottom: isEditing && !isLocked ? "1px" : "0",
        whiteSpace: "nowrap",
        transition: "color 0.15s",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {displayValue}
    </span>
  );
};

/* ─── ActionBtn ───────────────────────────────────────────────────────────── */
const ActionBtn = ({ icon, color, title, onClick, solid = false }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      height: "32px",
      padding: "0 14px",
      borderRadius: "8px",
      border: `1.5px solid ${color}`,
      backgroundColor: solid ? color : "transparent",
      color: solid ? "#fff" : color,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "5px",
      fontSize: "12px",
      fontWeight: "600",
      transition: "all 0.18s",
      letterSpacing: "0.02em",
      fontFamily: "'DM Sans', sans-serif",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.opacity = "0.82";
      e.currentTarget.style.transform = "translateY(-1px)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.opacity = "1";
      e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    {icon}
    <span>{title}</span>
  </button>
);

/* ─── FlatRow ─────────────────────────────────────────────────────────────── */
const FlatRow = ({ row, isEditing, isGrandTotal, onEdit }) => {
  const isSummary = row._isSummary;
  const locked    = isGrandTotal || isSummary;
  const variance  = (row.actual || 0) - (row.forecastBudget || 0);
  const varColor  = getVarianceColor(variance);
  const varBg     = getVarianceBg(variance);

  const borderRight = `1px solid ${T.border}`;
  const cellBase = {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "0 12px", borderRight, overflowX: "auto",
    boxSizing: "border-box",
  };

  const rowBg = isGrandTotal ? "#eef6f5" : isSummary ? "#edf2f9" : T.white;
  const rowHeight = isGrandTotal ? "52px" : isSummary ? "44px" : "50px";

  return (
    <div
      className={isSummary ? "est-summary-row" : isGrandTotal ? "" : "est-detail-row"}
      style={{
        display: "flex", alignItems: "stretch", height: rowHeight,
        backgroundColor: rowBg,
        borderBottom: isGrandTotal ? "none" : `1px solid ${T.border}`,
        transition: "background-color 0.15s",
        position: "relative",
        minWidth: "1200px",
      }}
    >
      {isSummary && (
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "3px", backgroundColor: T.teal, borderRadius: "0 2px 2px 0" }} />
      )}
      {isGrandTotal && (
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "3px", backgroundColor: T.navy, borderRadius: "0 2px 2px 0" }} />
      )}

      {/* Date */}
      <div style={{ ...cellBase, width: "100px", flexShrink: 0 }}>
        {locked
          ? <span style={{ fontSize: "12px", color: T.textLight }}>—</span>
          : <EditableText value={row.expenseDate} isEditing={isEditing} isLocked={false} onCommit={(v) => onEdit("expenseDate", v)} type="date" />
        }
      </div>

      {/* Category */}
      <div style={{ ...cellBase, width: "250px", flexShrink: 0 }}>
        <span style={{
          fontSize: "12.5px",
          fontWeight: isGrandTotal || isSummary ? "700" : "500",
          color: isGrandTotal ? T.navy : isSummary ? T.teal : T.textMuted,
          whiteSpace: "nowrap",
          letterSpacing: isSummary || isGrandTotal ? "0.02em" : "0",
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {isGrandTotal ? "Total Expense" : (row.categoryName || "—")}
        </span>
      </div>

      {/* Sub-Category */}
      <div style={{ ...cellBase, width: "250px", flexShrink: 0 }}>
        <span style={{
          fontSize: "12.5px", fontWeight: "500",
          color: (!locked && row.subCategoryName && row.subCategoryName !== "—") ? T.textMid : T.textLight,
          whiteSpace: "nowrap",
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {locked ? "—" : (row.subCategoryName || "—")}
        </span>
      </div>

      {/* Vendor */}
      <div style={{ ...cellBase, width: "180px", flexShrink: 0 }}>
        {locked
          ? <span style={{ fontSize: "12px", color: T.textLight }}>—</span>
          : <EditableText value={row.paidTo} isEditing={isEditing} isLocked={false} onCommit={(v) => onEdit("paidTo", v)} placeholder="—" />
        }
      </div>

      {/* Forecast */}
      <div style={{ ...cellBase, width: "110px", flexShrink: 0 }}>
        <EditableText value={row.forecastBudget} isEditing={isEditing} isLocked={locked} onCommit={(v) => onEdit("forecastBudget", v)} type="number" />
      </div>

      {/* Actual */}
      <div style={{ ...cellBase, width: "110px", flexShrink: 0 }}>
        <EditableText value={row.actual} isEditing={isEditing} isLocked={locked} onCommit={(v) => onEdit("actual", v)} type="number" />
      </div>

      {/* Variance */}
      <div style={{ ...cellBase, width: "110px", flexShrink: 0, backgroundColor: varBg, borderRight }}>
        <span style={{
          fontSize: "12.5px", fontWeight: "700", color: varColor,
          display: "flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap",
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {variance !== 0 && <span style={{ fontSize: "9px", opacity: 0.8 }}>{variance > 0 ? "▲" : "▼"}</span>}
          {formatCurrency(variance)}
        </span>
      </div>

      {/* Payment Mode */}
      <div style={{ ...cellBase, width: "110px", flexShrink: 0, borderRight: "none" }}>
        {locked
          ? <span style={{ fontSize: "12px", color: T.textLight }}>—</span>
          : <EditableText value={row.paymentMode} isEditing={isEditing} isLocked={false} onCommit={(v) => onEdit("paymentMode", v)} placeholder="—" />
        }
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const ExpenseStatementTable = ({ data = [], month = "" }) => {
  const [isEditing,       setIsEditing]       = useState(false);
  const [rows,            setRows]            = useState([]);
  const [originalRows,    setOriginalRows]    = useState([]);
  const [feedbackMsg,     setFeedbackMsg]     = useState("");
  const [showFeedback,    setShowFeedback]    = useState(false);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [refetch,         setRefetch]         = useState(0);

  const toast = (msg) => {
    setFeedbackMsg(msg);
    setShowFeedback(true);
    setTimeout(() => setShowFeedback(false), 2500);
  };

  useEffect(() => {
    setDetailedLoading(true);
    const monthParam = month || moment().format("MMM YYYY");
    getExpenseDeviation(
      monthParam,
      (response) => {
        const flat = flattenApiResponse(Array.isArray(response) ? response : []);
        setRows(deepClone(flat));
        setOriginalRows(deepClone(flat));
        setDetailedLoading(false);
      },
      () => {
        setRows([]);
        setOriginalRows([]);
        setDetailedLoading(false);
        toast("Failed to load data");
      },
    );
  }, [month, refetch]);

  const updateRow = (key, field, value) => {
    setRows((prev) => {
      if (field === "forecastBudget") {
        const target = prev.find((r) => r._key === key);
        if (!target) return prev;
        const { categoryName, subCategoryName } = target;
        return prev.map((row) =>
          row.categoryName === categoryName && row.subCategoryName === subCategoryName
            ? { ...row, [field]: value }
            : row
        );
      }
      return prev.map((r) => r._key === key ? { ...r, [field]: value } : r);
    });
  };

  const handleSave = () => {
    setIsEditing(false);
    const parsedMonth  = moment(month, ["MMM YYYY", "MMMM YYYY", "YYYY-MM", "MM-YYYY"], true);
    const currentMonth = parsedMonth.isValid() ? parsedMonth.format("MMM YYYY") : (month || moment().format("MMM YYYY"));

    const seen  = new Set();
    const items = [];

    rows.filter((r) => !r._isSummary).forEach((r) => {
      const dedupKey = `${r.categoryName}__${r.subCategoryName}__${r.expenseDate}`;
      if (seen.has(dedupKey)) return;
      seen.add(dedupKey);
      items.push({
        category:       r.categoryName,
        subCategory:    r.subCategoryName === "—" ? "" : (r.subCategoryName || ""),
        forecastAmount: r.forecastBudget || 0,
        actualAmount:   r.actual         || 0,
        expenseDate:    r.expenseDate    || "",
        paidTo:         r.paidTo         || "",
        paymentMode:    r.paymentMode    || "",
      });
    });

    saveBulkExpenseDeviation(
      { month: currentMonth, items },
      ()      => { toast("Saved successfully ✓"); setRefetch((n) => n + 1); },
      (error) => toast(error?.response?.data?.message || error?.message || "Error saving data"),
    );
  };

  const handleDiscard = () => { setIsEditing(false); setRows(deepClone(originalRows)); toast("Discarded"); };
  const handleReset   = () => { setRows(deepClone(originalRows)); toast("Reset to original"); };

  const detailRows    = rows.filter((r) => !r._isSummary);
  const uniqueBudgets = new Map();
  detailRows.forEach((r) => {
    const key = `${r.categoryName}__${r.subCategoryName}`;
    if (!uniqueBudgets.has(key)) uniqueBudgets.set(key, r.forecastBudget || 0);
  });
  const grandForecast = Array.from(uniqueBudgets.values()).reduce((s, v) => s + v, 0);
  const grandActual   = detailRows.reduce((s, r) => s + (r.actual || 0), 0);

  const toastBg =
    feedbackMsg.includes("Saved")                                       ? T.teal :
    feedbackMsg.includes("Reset") || feedbackMsg.includes("Discarded")  ? "#718096" :
    T.red;

  const displayMonth = moment(month, ["MMM YYYY", "MMMM YYYY"]).isValid()
    ? moment(month, ["MMM YYYY", "MMMM YYYY"]).format("MMM YYYY")
    : month;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

        .est-detail-row:hover  { background-color: #f0f7f6 !important; }
        .est-summary-row:hover { background-color: #e3eaf5 !important; }
        .est-header-cell { user-select: none; }

        .est-table-wrap ::-webkit-scrollbar { height: 6px; width: 6px; }
        .est-table-wrap ::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 4px; }
        .est-table-wrap ::-webkit-scrollbar-thumb { background: #b2d8d8; border-radius: 4px; }
        .est-table-wrap ::-webkit-scrollbar-thumb:hover { background: #025954; }
        .est-table-wrap ::-webkit-scrollbar-corner { background: #f1f5f9; }

        @keyframes softPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        .edit-indicator { animation: softPulse 2s ease-in-out infinite; }

        @keyframes shimmer {
          0% { background-position: -600px 0; }
          100% { background-position: 600px 0; }
        }
        .shimmer-line {
          background: linear-gradient(90deg, #f0f4f8 25%, #e2ecf0 50%, #f0f4f8 75%);
          background-size: 600px 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 6px;
        }

        @media (max-width: 1024px) {
          .est-table-content {
            overflow-x: auto;
            min-width: 100%;
          }
          .est-table-scroll {
            width: fit-content;
            min-width: 100%;
          }
        }

        @media (max-width: 768px) {
          .est-table-wrap {
            margin: 8px !important;
            border-radius: 8px !important;
          }
          .est-table-header-title {
            font-size: 12px !important;
          }
          .est-table-header-actions {
            gap: 6px !important;
          }
          .est-table-header-actions button {
            padding: 4px 10px !important;
            font-size: 11px !important;
          }
        }

        @media (max-width: 600px) {
          .est-table-wrap {
            margin: 4px !important;
            border-radius: 6px !important;
          }
          .est-table-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          .est-table-header-title {
            font-size: 11px !important;
          }
          .est-table-header-actions button {
            padding: 3px 8px !important;
            font-size: 10px !important;
          }
        }
      `}</style>

      <div
        className="est-table-wrap"
        style={{
          fontFamily: "'DM Sans', sans-serif",
          borderRadius: "14px",
          margin: '15px',
          overflow: "hidden",
          border: `1px solid ${T.borderDark}`,
          boxShadow: "0 4px 24px rgba(38,49,69,0.10), 0 1px 3px rgba(38,49,69,0.06)",
          backgroundColor: T.white,
          maxWidth: "100%",
        }}
      >
        {/* ── Title Bar ──────────────────────────────────────────────── */}
        <div className="est-table-header" style={{
          backgroundColor: T.navy,
          padding: "14px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}>
          <div className="est-table-header-title" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              width: "3px", height: "22px",
              background: `linear-gradient(180deg, ${T.tealMid}, ${T.teal})`,
              borderRadius: "2px",
            }} />
            <span style={{ color: "#fff", fontSize: "14px", fontWeight: "700", letterSpacing: "0.02em", fontFamily: "'DM Sans', sans-serif" }}>
              Expense Statement
            </span>
            {month && (
              <span style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "#cde8e6",
                fontSize: "11px", fontWeight: "600",
                padding: "3px 10px", borderRadius: "20px",
                letterSpacing: "0.05em",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {displayMonth}
              </span>
            )}
          </div>

          <div className="est-table-header-actions" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {showFeedback && (
              <div style={{
                padding: "5px 14px", borderRadius: "8px",
                fontSize: "12px", fontWeight: "600",
                backgroundColor: toastBg, color: "#fff",
                boxShadow: `0 4px 12px ${toastBg}55`,
                letterSpacing: "0.01em",
                fontFamily: "'DM Sans', sans-serif",
              }}>
                {feedbackMsg}
              </div>
            )}

            {isEditing ? (
              <div style={{ display: "flex", gap: "8px" }}>
                <ActionBtn icon={<RotateCcw size={13} />} color="rgba(255,255,255,0.7)" title="Reset"   onClick={handleReset} />
                <ActionBtn icon={<X         size={13} />} color="#ff7b6b"               title="Discard" onClick={handleDiscard} />
                <ActionBtn icon={<Save      size={13} />} color={T.tealMid}             title="Save"    onClick={handleSave} solid />
              </div>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "7px 16px",
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  borderRadius: "8px",
                  color: "#fff",
                  fontSize: "12px", fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.18s",
                  letterSpacing: "0.02em",
                  fontFamily: "'DM Sans', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.18)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.10)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <Edit size={13} /> Edit
              </button>
            )}
          </div>
        </div>

        {/* ── Table Content Wrapper for Horizontal Scroll ──────────────── */}
        <div className="est-table-content" style={{ overflowX: "auto" }}>
          <div className="est-table-scroll" style={{ minWidth: "100%", width: "fit-content" }}>
            {/* ── Column Headers ─────────────────────────────────────────── */}
            <div style={{
              display: "flex", alignItems: "stretch", height: "40px",
              backgroundColor: "#f0f5fb",
              borderBottom: `2px solid ${T.borderDark}`,
              position: "sticky", top: 0, zIndex: 5,
              minWidth: "1200px",
            }}>
              {COLUMNS.map((col, i) => (
                <div
                  key={col.key}
                  className="est-header-cell"
                  style={{
                    width: col.key === "date" ? "100px" : col.key === "category" || col.key === "subcategory" ? "250px" : col.key === "vendor" ? "180px" : "110px",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "0 12px",
                    fontSize: "10.5px", fontWeight: "700",
                    color: T.textMuted,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    borderRight: i < COLUMNS.length - 1 ? `1px solid ${T.border}` : "none",
                    gap: "4px",
                    fontFamily: "'DM Sans', sans-serif",
                    boxSizing: "border-box",
                    flexShrink: 0,
                  }}
                >
                  {col.label}
                  {isEditing && ["forecast", "actual", "vendor", "payment", "date"].includes(col.key) && (
                    <span className="edit-indicator" style={{ color: T.teal, fontSize: "10px" }}>✎</span>
                  )}
                </div>
              ))}
            </div>

            {/* ── Body ───────────────────────────────────────────────────── */}
            {detailedLoading ? (
              <div style={{ padding: "24px 20px", display: "flex", flexDirection: "column", gap: "10px", minWidth: "1200px" }}>
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="shimmer-line" style={{ height: "44px", opacity: 1 - i * 0.18 }} />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div style={{
                padding: "60px 20px", textAlign: "center",
                display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
                backgroundColor: T.surfaceAlt,
                minWidth: "1200px",
              }}>
                <div style={{ fontSize: "36px", opacity: 0.4 }}>📋</div>
                <div style={{ fontSize: "14px", fontWeight: "500", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                  No data available for the selected period
                </div>
              </div>
            ) : (
              <div style={{ minWidth: "1200px" }}>
                {rows.map((row) => (
                  <div key={row._key} className={row._isSummary ? "est-summary-row" : "est-detail-row"}>
                    <FlatRow
                      row={row}
                      isEditing={isEditing}
                      isGrandTotal={false}
                      onEdit={(field, value) => updateRow(row._key, field, value)}
                    />
                  </div>
                ))}

                {/* Grand Total */}
                <div style={{ borderTop: `2px solid ${T.borderDark}` }}>
                  <FlatRow
                    row={{
                      _key: "__grand_total__",
                      _isSummary: false,
                      categoryName: "Total Expense",
                      subCategoryName: "",
                      forecastBudget: grandForecast,
                      actual: grandActual,
                      expenseDate: "", paidTo: "", paymentMode: "",
                    }}
                    isEditing={false}
                    isGrandTotal={true}
                    onEdit={() => {}}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpenseStatementTable;